import { createStyles, Theme } from '@material-ui/core'
import {
  colorSectionHeadingOnLight,
  fullsizeAbsolute,
  layoutContentMaxWidthPx,
  transition,
} from '../../../../shared/style-settings'
import { makeStyles, lighten, darken } from '@material-ui/core/styles'

export const useNonprofitCategoriesStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      background: `linear-gradient(180deg, ${lighten(
        theme.palette.primary.light,
        0.75
      )} 0%, ${lighten(theme.palette.primary.light, 0.9)} 100%)`,
      position: 'relative',
      // Use clip for decorative pseudo-elements, but allow slider content to be visible
      overflowX: 'clip',
      overflowY: 'visible',
      // Decorative background element
      '&::before': {
        content: '""',
        position: 'absolute',
        top: -100,
        right: -100,
        width: 300,
        height: 300,
        borderRadius: '50%',
        background: `radial-gradient(circle, ${lighten(
          theme.palette.primary.light,
          0.6
        )} 0%, transparent 70%)`,
        opacity: 0.5,
        pointerEvents: 'none',
      },
      '&::after': {
        content: '""',
        position: 'absolute',
        bottom: -50,
        left: -50,
        width: 200,
        height: 200,
        borderRadius: '50%',
        background: `radial-gradient(circle, ${lighten(
          theme.palette.primary.light,
          0.5
        )} 0%, transparent 70%)`,
        opacity: 0.4,
        pointerEvents: 'none',
      },
    },
    sliderHoder: {
      padding: theme.spacing(4, 3, 8, 3),
      maxWidth: layoutContentMaxWidthPx,
      minHeight: 270,
      margin: '0 auto',
      position: 'relative',
      [theme.breakpoints.down('sm')]: {
        display: 'block',
        padding: theme.spacing(3, 2, 5, 2),
      },
      [theme.breakpoints.down('xs')]: {
        padding: theme.spacing(2, 1, 4, 1),
      },
    },

    // Embla Carousel styles
    embla: {
      position: 'relative',
      padding: theme.spacing(0, 7),
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(0, 5),
      },
      [theme.breakpoints.down('xs')]: {
        padding: theme.spacing(0, 4),
      },
    },
    emblaViewport: {
      overflow: 'hidden',
      paddingTop: theme.spacing(5),
    },
    emblaContainer: {
      display: 'flex',
      touchAction: 'pan-y',
      // Add padding to prevent last item from being cut off
      paddingRight: theme.spacing(2),
      [theme.breakpoints.down('sm')]: {
        paddingRight: theme.spacing(1),
      },
    },
    emblaSlide: {
      flex: '0 0 auto',
      minWidth: 0,
      // Prevent hover effects from being clipped
      overflow: 'visible',
      // Number of elements on different screens
      // Desktop (xl): 7 elements
      width: 'calc(100% / 7)',
      [theme.breakpoints.down('lg')]: {
        // Large screens (lg): 6 elements
        width: 'calc(100% / 6)',
      },
      [theme.breakpoints.down('md')]: {
        // Medium screens (md): 4 elements
        width: 'calc(100% / 4)',
      },
      [theme.breakpoints.down('sm')]: {
        // Small screens (sm): 3 elements
        width: 'calc(100% / 3)',
      },
      [theme.breakpoints.down('xs')]: {
        // Extra small screens (xs): 2 elements
        width: 'calc(100% / 2)',
      },
      [theme.breakpoints.down(400)]: {
        // Very small screens: 1 element
        width: 'calc(100% / 1)',
      },
    },
    emblaButton: {
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-50%)',
      background: `linear-gradient(135deg, ${
        theme.palette.primary.main
      } 0%, ${darken(theme.palette.primary.main, 0.15)} 100%)`,
      color: theme.palette.common.white,
      border: 'none',
      borderRadius: '50%',
      width: 48,
      height: 48,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      transition: transition,
      zIndex: 1,
      boxShadow: 'none',
      '&:hover': {
        background: `linear-gradient(135deg, ${darken(
          theme.palette.primary.main,
          0.1
        )} 0%, ${darken(theme.palette.primary.main, 0.25)} 100%)`,
        transform: 'translateY(-50%) scale(1.1)',
        boxShadow: 'none',
      },
      '&:active': {
        transform: 'translateY(-50%) scale(0.95)',
      },
      '&:disabled': {
        background: theme.palette.grey[300],
        cursor: 'not-allowed',
        transform: 'translateY(-50%)',
        boxShadow: 'none',
        opacity: 0.6,
        '&:hover': {
          transform: 'translateY(-50%)',
          boxShadow: 'none',
        },
      },
      '& .MuiSvgIcon-root': {
        fontSize: 28,
        transition: transition,
      },
      '&:hover .MuiSvgIcon-root': {
        transform: 'scale(1.1)',
      },
      [theme.breakpoints.down('sm')]: {
        width: 40,
        height: 40,
        '& .MuiSvgIcon-root': {
          fontSize: 24,
        },
      },
    },
    emblaButtonPrev: {
      left: 8,
      [theme.breakpoints.down('sm')]: {
        left: 4,
      },
    },
    emblaButtonNext: {
      right: 8,
      [theme.breakpoints.down('sm')]: {
        right: 4,
      },
    },
    emblaDots: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      marginTop: theme.spacing(3),
      gap: theme.spacing(1.5),
    },
    emblaDot: {
      width: 10,
      height: 10,
      borderRadius: '50%',
      border: `2px solid ${theme.palette.primary.light}`,
      background: 'transparent',
      cursor: 'pointer',
      padding: 0,
      transition: transition,
      '&:hover': {
        background: lighten(theme.palette.primary.light, 0.3),
        transform: 'scale(1.2)',
        borderColor: theme.palette.primary.main,
      },
      [theme.breakpoints.down('sm')]: {
        display: 'none',
      },
    },
    emblaDotActive: {
      background: theme.palette.primary.main,
      borderColor: theme.palette.primary.main,
      transform: 'scale(1.3)',
      boxShadow: 'none',
    },

    sliderItem: {
      cursor: 'pointer',
      transition: transition,
      padding: theme.spacing(1),
      borderRadius: theme.shape.borderRadius * 2,
      '&:hover': {
        transform: 'translateY(-8px)',
      },
      '&:hover $sliderImgWrapper': {
        transform: 'scale(1.08)',
        boxShadow: 'none',
        '&::after': {
          opacity: 0.15,
        },
      },
      '&:hover $sliderItemTitle': {
        color: theme.palette.primary.main,
      },
    },

    sliderItemImg: {},
    sliderItemTitle: {
      margin: '0 auto',
      textAlign: 'center',
      fontWeight: 600,
      fontSize: 'clamp(12px, 1.2vw, 15px)',
      color: colorSectionHeadingOnLight,
      transition: transition,
      padding: theme.spacing(0, 1),
      lineHeight: 1.4,
      maxWidth: 140,
      [theme.breakpoints.down('sm')]: {
        fontSize: 12,
      },
    },
    sliderImgWrapper: {
      backgroundSize: 'cover',
      backgroundPosition: '50% 50%',
      position: 'relative',
      padding: 15,
      background: `linear-gradient(145deg, ${lighten(
        theme.palette.primary.light,
        0.6
      )} 0%, ${lighten(theme.palette.primary.light, 0.4)} 100%)`,
      borderRadius: '50%',
      width: 120,
      height: 120,
      margin: '0 auto',
      marginBottom: theme.spacing(2.5),
      overflow: 'hidden',
      transition: transition,
      boxShadow: 'none',
      border: `3px solid ${lighten(theme.palette.primary.light, 0.7)}`,
      // Decorative overlay
      '&::after': {
        content: '""',
        ...fullsizeAbsolute,
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, transparent 50%)`,
        opacity: 0,
        transition: transition,
        borderRadius: '50%',
        pointerEvents: 'none',
      },
      [theme.breakpoints.down('sm')]: {
        width: 100,
        height: 100,
        marginBottom: theme.spacing(2),
      },
      [theme.breakpoints.down('xs')]: {
        width: 90,
        height: 90,
      },
    },
    sliderHolder: {
      width: 110,
      borderRadius: '50%',
      overflow: 'hidden',
    },

    sliderImg: {
      display: 'block',
      width: '100%',
    },
    loader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    imgError: {
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: theme.palette.primary.main,
      '& .MuiSvgIcon-root': {
        fontSize: 40,
        opacity: 0.7,
        transition: transition,
      },
      '$sliderItem:hover & .MuiSvgIcon-root': {
        transform: 'scale(1.1)',
        opacity: 1,
      },
      [theme.breakpoints.down('sm')]: {
        '& .MuiSvgIcon-root': {
          fontSize: 32,
        },
      },
    },
    imgBlock: {
      ...fullsizeAbsolute,
      backgroundSize: 'cover',
      backgroundPosition: '50% 50%',
      transition: transition,
      '$sliderItem:hover &': {
        transform: 'scale(1.05)',
      },
    },
  })
)
